import type { FieldComponent } from '@atlas/react-forms';
import cx from 'classnames';
import { type ChangeEvent, useCallback, useState } from 'react';
import { NumericFormat } from 'react-number-format';

import { useTranslation } from 'utils/translations';

import Errors from '../../Errors';

import styles from './MoneyInput.module.scss';

const decimalSeparators = [',', '.'];
const fractionDigits = 2;

const MoneyInput: FieldComponent = ({ onChange, field, onFocus, onBlur }) => {
  const [showError, setShowError] = useState<boolean>(false);

  const initialValue = field.value?.toString() ?? '';
  const placeholder = useTranslation(field.label.toLowerCase()); // get a11y by field.label
  const isValidField = !field?.errors?.length;

  const handleChangeValue = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.target.value, true);
    },
    [onChange],
  );

  const handleBlur = useCallback(() => {
    onBlur?.();
    setShowError(true);
  }, [onBlur]);

  const handleFocus = useCallback(() => {
    onFocus?.();
    setShowError(false);
  }, [onFocus]);

  const placeholderText = `${placeholder}${field.isRequired ? ' *' : ''}`;

  return (
    <div className={styles['form-field-container']}>
      <NumericFormat
        value={initialValue}
        onChange={handleChangeValue}
        type="text"
        autoFocus={field.autofocus}
        name={field.name}
        allowedDecimalSeparators={decimalSeparators}
        id={field.name}
        allowNegative={false}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={field.disabled}
        placeholder={placeholderText}
        data-testid="money-input"
        inputMode="decimal"
        decimalScale={fractionDigits}
        className={cx(styles['money-input'], {
          [styles.error]: !isValidField && showError,
        })}
      />
      {showError && (
        <div className={styles['field-errors']}>
          <Errors errors={field.errors} />
        </div>
      )}
    </div>
  );
};

export default MoneyInput;
